import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable} from 'rxjs/Observable';
import { catchError, map, retryWhen, finalize } from 'rxjs/operators';
import { IMultiSelectOption } from 'angular-2-dropdown-multiselect';

import { CivilCase } from './civil-case';
import { CivilIdentityOtherData } from'./civil-identity-other-data';
import { CivilIdentitySearchCase } from './civil-identity-search-case';
import { CivilIdentitySearchResponse } from './civil-identity-search-response';
import { CivilSearchConstants } from './civil-search-constants';
import { CaseIdentifier } from './../shared/case-identifier';
import { CountyService } from '../shared/county.service';
import { environment } from './../../environments/environment';
import { FileNumberUtil } from './../shared/file-number-util';
import { CivilPartyNameSearchResponse } from './civil-party-name-search-response';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';
import { WrappedResponse } from '../shared/wrapped-response';
import { DatePipe } from '@angular/common';
import { CivilIdentityAdditionalData } from './civil-identity-additional-data';
import { SafeJSON } from '../shared/safe-json';
import { CivilIdentiyAdditionalDetails } from './civil-identity-additional-details';

@Injectable()
export class CivilSearchService {

  static getCivilStartYear(): number {
    return 1966;
  }
  static getCurrentYear(): number {
    return new Date().getFullYear();
  }

  static getCaseTypes(caseCategory: string): IMultiSelectOption[] {
    if (caseCategory === 'Civil Actions') {
      return CivilSearchConstants.case_types_for_civil_actions;
    } else if (caseCategory === 'Estates') {
      return CivilSearchConstants.case_types_for_estates;
    } else if (caseCategory === 'Special Proceedings') {
      return CivilSearchConstants.case_types_for_special_proceedings;
    } else if (caseCategory === 'Registrations') {
      return CivilSearchConstants.case_types_for_registrations;
    } else if ( caseCategory === 'Miscellaneous') {
      return CivilSearchConstants.case_types_for_miscellaneous;
    }
    return CivilSearchConstants.case_types;
  }

  static getIssueTypes(): IMultiSelectOption[] {
    return CivilSearchConstants.issue_types;
  }

  static translateIMultiSelectOptionId(id: string, list: IMultiSelectOption[]): string {
    const match = list.find(item => item.id === id);
    return match ? match.name : '';
  }

  static getPartySearchCriteriaDescription(searchForm: any) {
    const criteria = [];

    if (searchForm.counties) {
      const countiesDescription =
        (searchForm.counties.length > 0 && searchForm.counties.length < CountyService.number_of_counties) ?
          searchForm.counties.join(', ') : 'ALL';
      criteria.push('Counties = "' + countiesDescription + '"');
    }
    if (searchForm.searchBusinessNames && searchForm.businessName) {
      const businessNm: string = CivilSearchService.normalizeNameInput(searchForm.businessName.toUpperCase());
      criteria.push('Business Name = "' + businessNm + '" (' + searchForm.searchNameMatchType + ')');
    } else {
      if (searchForm.firstName) {
        const firstNm: string = CivilSearchService.normalizeNameInput(searchForm.firstName.toUpperCase());
        criteria.push('First Name = "' + firstNm + '" (' + searchForm.searchNameMatchType + ')');
      }
      if (searchForm.middleName) {
        const middleNm: string = CivilSearchService.normalizeNameInput(searchForm.middleName.toUpperCase());
        criteria.push('Middle Name = "' + middleNm + '" (' + searchForm.searchNameMatchType + ')');
      }
      if (searchForm.lastName) {
        const lastNm: string = CivilSearchService.normalizeNameInput(searchForm.lastName.toUpperCase());
        criteria.push('Last Name = "' + lastNm + '" (' + searchForm.searchNameMatchType + ')');
      }
    }
    if (searchForm.advancedDateFilter) {
      if ( searchForm.partyEnteredDate ) {
        criteria.push('Entered date = "' + this.convertDateToString(searchForm.partyEnteredDate) + '"');
      }
    } else {
      if (CivilSearchService.getCivilStartYear() !== searchForm.fromYear
         || CivilSearchService.getCurrentYear() !== searchForm.toYear) {
          criteria.push('Case Years = "' + searchForm.fromYear + '" to "' + searchForm.toYear + '"');
      }
    }
    if (searchForm.caseTypes) {
      const caseTypesDescription =
        (searchForm.caseTypes.length > 0 && searchForm.caseTypes.length < CivilSearchService.getCaseTypes(null).length) ?
          searchForm.caseTypes
            .map(c => CivilSearchService.translateIMultiSelectOptionId(c, CivilSearchConstants.case_types)).join(', ') : 'ALL';
      criteria.push('Case Types = "' + caseTypesDescription + '"');
    }

    if (searchForm.issueTypes) {
      const issueTypesDescription =
        (searchForm.issueTypes.length > 0 &&  searchForm.issueTypes.length < CivilSearchService.getIssueTypes().length ) ?
          searchForm.issueTypes
            .map(i => CivilSearchService.translateIMultiSelectOptionId(i, CivilSearchConstants.issue_types)).join(', ') : 'ALL';
      criteria.push('Issue Types = "' + issueTypesDescription + '"');
    }

    const result = criteria.join('  | ');
    return result;
  }

  public static normalizeNameInput(input: string): string {
    const str = input || '';
    return str.replace(/^\s+|\s+$/gm, '').replace(/\s+/gm, ' ');
  }

  constructor(private http: HttpClient) { }

  findByCaseIdentifier(caseIdentifier: CaseIdentifier): Observable<CivilCase> {
    let url = environment.proxyBaseUrl +
              'civil_cases/cases?county_name=' +
              caseIdentifier.county +
              '&file_number=' +
              FileNumberUtil.normalizeCaseNumber(caseIdentifier.caseNumber);
    const startTime = Date.now();
    return this.http.get<WrappedResponse>(url).pipe(
      map(response => new CivilCase(response.data)),
      catchError(this.handleError),
      finalize(() => {
        // logging response time
        if (!environment.production) {
          const elapsed = Date.now() - startTime;
          console.log('Response Elapsed Time = ' + elapsed );
        }
     })
    );
  }

  /**
   * search the party additional data in batch
   * @param civilIdentitySearchCaseList
   * @param indexedJudgmentOnly
   */
  searchPartyAdditionalData(civilIdentitySearchCaseList: CivilIdentitySearchCase[],
    indexedJudgmentOnly: boolean): Observable<Map<string, CivilIdentityAdditionalData>> {

    // hard-coded direct ws url
    // const url = ' https://dev.nccourts.org/case_search_hadoop_ws_1_23_0-rc_1/rest/v1/additional_party_data_requests';

    // proxy ws url
    const url = environment.proxyBaseUrl + 'civil_cases/additional_party_data_requests/';
    const requestData: any[] = this. buildAdditionalRequestData(civilIdentitySearchCaseList, indexedJudgmentOnly);
    if (!environment.production) {
       console.log('request data=');
       console.log(requestData);
    }
    const startTime = Date.now();
    return this.http.post<WrappedResponse>(url, requestData).pipe(
      /*
        retryWhen(function(errors) { // retry 5xx errors once after a 250ms delay
          return errors.scan(function(errorCount, err) {
              const shouldRetry = err instanceof HttpErrorResponse && err.status >= 500;
              if (!shouldRetry || errorCount >= 1) {
                  throw err;
              }
              return errorCount + 1;
          }, 0).delay(250);
        }), */
        map(response => this.buildAdditionalResponseData(response)),
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          const errorMessage
            = (error.error && error.error.messages && error.error.messages.map(v => v.message).join('; ')) || 'error encountered';
          console.error(`errorMessage=${errorMessage}`);
          let errorMap: Map<string, CivilIdentityAdditionalData> = new Map<string, CivilIdentityAdditionalData>();
          let errorAdditionalData: CivilIdentityAdditionalData = new CivilIdentityAdditionalData('error', null, errorMessage);
          errorMap.set('error', errorAdditionalData);
          return  Observable.of<Map<string, CivilIdentityAdditionalData>>(errorMap);
        }),
        finalize(() => {
          // logging response time
          if (!environment.production) {
            console.log('Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
          }
        })
    );
  }

  /**
   * search the party additional data in batch
   * @param requestJsonData
   * @param indexedJudgmentOnly
   */
  searchPartyAdditionalDataWithJsonRequest(requestJsonData: any[]): Observable<Map<string, CivilIdentityAdditionalData>> {
    // proxy ws url
    const url = environment.proxyBaseUrl + 'civil_cases/additional_party_data_requests/';
    if (!environment.production) {
       console.log('request data=');
       console.log(requestJsonData);
    }
    const startTime = Date.now();
    return this.http.post<WrappedResponse>(url, requestJsonData).pipe(
      /*
        retryWhen(function(errors) { // retry 5xx errors once after a 250ms delay
          return errors.scan(function(errorCount, err) {
              const shouldRetry = err instanceof HttpErrorResponse && err.status >= 500;
              if (!shouldRetry || errorCount >= 1) {
                  throw err;
              }
              return errorCount + 1;
          }, 0).delay(250);
        }), */
        map(response => {
           const resMap: Map<string, CivilIdentityAdditionalData> = this.buildAdditionalResponseData(response);
           if (!environment.production) {  // logging if needed
             console.log('Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
           }
           return resMap;
        }),
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          const errorMessage
            = (error.error && error.error.messages && error.error.messages.map(v => v.message).join('; ')) || 'error encountered';
          console.error(`errorMessage=${errorMessage}`);
          let errorMap: Map<string, CivilIdentityAdditionalData> = new Map<string, CivilIdentityAdditionalData>();
          let errorAdditionalData: CivilIdentityAdditionalData = new CivilIdentityAdditionalData('error', null, errorMessage);
          errorMap.set('error', errorAdditionalData);
          return  Observable.of<Map<string, CivilIdentityAdditionalData>>(errorMap);
        }),
        finalize(() => {
          // logging response time
          if (!environment.production) {  // logging if needed
            // console.log('Addtional Response Elapsed Time = ' + (Date.now() - startTime) );
          }
        })
    );
  }

  /**
   * search the other data such as In-Regards-to or Opposing-Party
   * @param civilIdentitySearchCase
   * @param indexedJudgmentOnly
   */
  searchOtherData(civilIdentitySearchCase: CivilIdentitySearchCase, indexedJudgmentOnly: boolean): Observable<CivilIdentityOtherData> {
    const url = environment.proxyBaseUrl +
                'civil_cases/party/' +
                (indexedJudgmentOnly ? 'opposing_party' : 'in_regards_to');
    let params = new HttpParams();

    // formed the other parameters
    const strippedRealCaseNumber =  FileNumberUtil.stripMiddleZeros(civilIdentitySearchCase.getRealCaseNumber().trim());
    params = params.append('case_key', strippedRealCaseNumber);
    // TODO: 20180329 shouldn't all these fields below have been trimmed prior to us accessing them?
    params = params.append('role', civilIdentitySearchCase.getPartyRoleCode().trim());
    params = params.append('number', civilIdentitySearchCase.getPartyNumber().toString());
    params = params.append('entts', civilIdentitySearchCase.getPartyEntryTime().trim());
    params = params.append('type', civilIdentitySearchCase.getRowOrigin().trim());
    params = params.append('other_entts', civilIdentitySearchCase.getOtherEntryTime().trim());
    return this.http.get<WrappedResponse>(url, { params }).pipe(
      // TODO: 20180329 can't we remove this retry logic now that the backend has matured?
      retryWhen(function(errors) { // retry 5xx errors once after a 250ms delay
        return errors.scan(function(errorCount, err) {
            const shouldRetry = err instanceof Response && err.status >= 500;
            if (!shouldRetry || errorCount >= 1) {
                throw err;
            }
            return errorCount + 1;
        }, 0).delay(250)}),
        map(response => new CivilIdentityOtherData(response.data)),
        catchError((error: HttpErrorResponse) => {
          const errorMessage
            = (error.error && error.error.messages && error.error.messages.map(v => v.message).join('; ')) || 'error encountered';
          console.error(`errorMessage=${errorMessage}`);
          const civilOther =  new CivilIdentityOtherData(null);
          civilOther.errorMessage = errorMessage;
          return  Observable.of<CivilIdentityOtherData>(civilOther);
        })
    );
  }

  /**
   *  party name search by given parameters and endpoint name
   */
  partyNameSearch(params: HttpParams, endpointName: string): Observable<CivilIdentitySearchResponse> {
    // form the url based on the given parameters and endpointname
    const url = `${environment.proxyBaseUrl}civil_case_identities/parties/${endpointName}`;
    if (!environment.production) {
      console.log('URL=' + url + '?' + params);
    }

    return this.http.get<CivilPartyNameSearchResponse>(url, {params})
        .pipe(
            retryWhen(function(errors) { // retry 5xx errors once after a 250ms delay
              return errors.scan(function(errorCount, err) {
                  const shouldRetry = err instanceof HttpErrorResponse && err.status >= 500;
                  if (!shouldRetry || errorCount >= 1) {
                      throw err;
                  }
                  return errorCount + 1;
              }, 0).delay(250);
            }),
            map(partyNameResponse => this.buildIdentitySearchResponse(partyNameResponse)),
            catchError(this.handleError)
        );
  }

  getSearchRecordLastUpdateDate(): Observable<string> {
    const url = environment.proxyBaseUrl + 'civil_case_identities/parties/last_update';
    return this.http.get<WrappedResponse>(url).pipe(
      map(response => {
         let timestampNameIndex: number = response.data.lastUpdateTs.indexOf('"lastUpdateTimestamp":"');
         let timestampStartString: string = response.data.lastUpdateTs.substr(timestampNameIndex + '"lastUpdateTimestamp":"'.length);
         let timestampEndIndex: number = timestampStartString.indexOf('"},');
         let timestampString: string = timestampStartString.substr(0, timestampEndIndex);

         if (!environment.production) {
           console.log('last update time response data string= ' +  response.data.lastUpdateTs);
           /*The json format for lastUpdateTs's value is a string, so the statements above is to retrive the timestamp string:
            "lastUpdateTs":
            "{"data":{"lastUpdateTimestamp":"2018-09-11 10:46:29.359"},"messages":[],"responseCode":200,"responseCodeDescription":"OK"}"
           */
           console.log('last update time stamp string = ' + timestampString );
        }
         return timestampString;
      }),
      catchError((error: HttpErrorResponse) => {
       const errorMessage = error.error || 'error encountered';
       console.log('load last update date errorMessage2='  + errorMessage);
       // set the default update date at date of 1:00AM
       const defaultUpdateDate: string = new Date().toJSON();
       return Observable.of(defaultUpdateDate);
      })
   );
  }

  private handleError(error: HttpErrorResponse) {
    // TODO: if this error handling is consistent across all http calls,
    // we can push this code to CiprsHttp and avoid repeating the logic
    let errorMessage: string;
    if (error.status === 0 ||
        error.status >= 500) { // TODO: this line is here because of nginx issues which no longer exist. Remove it?
      console.error('error code = ' + error.status, error);
      errorMessage = 'Unable to reach the server. Please try again later.';
    } else {
      const wrappedErrorResponse = error.error || {};
      if (wrappedErrorResponse.responseCode === 404) {
        errorMessage = 'No matching records were found.';
      } else {
        errorMessage = 'The server encountered an error'; // TODO: what should the user be directed to do?
      }
    }
    console.error('errorMessage=' + errorMessage);
    return new ErrorObservable(errorMessage);
  }


  // tslint:disable-next-line:member-ordering
  public static translateMatchType(matchTypeUiValue: string): string {
    if (!matchTypeUiValue)  {
       return 'EXACT';
    }
    return matchTypeUiValue.toUpperCase() === 'Exact Match'.toUpperCase() ? 'EXACT' : 'STARTSWITH';
  }

  private buildIdentitySearchResponse(partyNameResponse: CivilPartyNameSearchResponse) {
    const result: CivilIdentitySearchResponse = {
      cases: [],
      pageNumber: partyNameResponse.data.pageNumber,
      pageSize: partyNameResponse.data.pageSize,
      totalRecords: partyNameResponse.data.totalRecords
    };

    for (let jsonResult of partyNameResponse.data.documentList) {
      result.cases.push(new CivilIdentitySearchCase(jsonResult));
    }

    return result;
  }

  // tslint:disable-next-line:member-ordering
  static convertDateToString(date: Date): string {
    let datePipe = new DatePipe('en-US');
    return datePipe.transform(date, 'MM/dd/yyyy');
  }

  /**
   *
   * @param civilIdentitySearchCaseList
   * @param indexedJudgmentOnly
   */
  buildAdditionalRequestData(civilIdentitySearchCaseList: CivilIdentitySearchCase[], indexedJudgmentOnly: boolean): any[] {
    const additionalDataMap: Map<string, any> = new Map<string, any>();
    const indexedJudgmentFlag: string = indexedJudgmentOnly ? 'Y' : 'N';
    for ( let civilIdentitySearchCase of civilIdentitySearchCaseList) {
      let realCaseNumber: string = FileNumberUtil.stripMiddleZeros(civilIdentitySearchCase.getRealCaseNumber().trim());

      // get each additional data (if not stored, instantiate one and set to map)
      let additionalData: any = additionalDataMap.get(realCaseNumber);
      if ( !additionalData ) {
        additionalData = {
          caseNumber: realCaseNumber,
          additionalPartyDetailsList: []
        }
        additionalDataMap.set(realCaseNumber, additionalData);
      }

      // add the current details in json format to the detail list
      let datailsJson = {
        entTs: civilIdentitySearchCase.getPartyEntryTime().trim(),
        partyRole: civilIdentitySearchCase.getPartyRoleCode().trim(),
        partyNumber: civilIdentitySearchCase.getPartyNumber().toString(),
        nameType: civilIdentitySearchCase.getRowOrigin().trim(),
        otherEntTs: civilIdentitySearchCase.getOtherEntryTime().trim(),
        indexedJudgment: indexedJudgmentFlag
      }
      additionalData.additionalPartyDetailsList.push(datailsJson);
    }

    return Array.from(additionalDataMap.values());
  }

  /**
   * build the additional response data
   * @param json
   */
  buildAdditionalResponseData(json: any): Map<string, CivilIdentityAdditionalData>  {
    if (!environment.production) {
       console.log('response data = ');
       console.log(json);
    }
    let arrayList: any = json.dataList;
    let additionalMap: Map<string, CivilIdentityAdditionalData> = new Map<string, CivilIdentityAdditionalData>();
    for (let addtionalDataJson of arrayList) {
      let realCaseNumber = SafeJSON.get('caseNumber', addtionalDataJson);
      let additionalDetailsListJson: any =  SafeJSON.get('additionalPartyDetailsList', addtionalDataJson);
      let additionalDetailsList: CivilIdentiyAdditionalDetails[] = new Array<CivilIdentiyAdditionalDetails>();
      for (let addtionalDetailJson of additionalDetailsListJson) {
        additionalDetailsList.push(new CivilIdentiyAdditionalDetails(addtionalDetailJson));
      }

      let additionalData: CivilIdentityAdditionalData = new CivilIdentityAdditionalData(realCaseNumber, additionalDetailsList);
      additionalMap.set(realCaseNumber, additionalData);
    }
    return additionalMap;
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/civil/civil-search.service.ts